import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, FileText, Calendar, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { highlightTextWithCount, extractContextSnippet, fuzzyMatch, stripHtmlTags } from "@/utils/highlightText";

interface SearchResult {
  id: string;
  type: 'blog' | 'service' | 'resource';
  title: string;
  excerpt: string;
  content?: string;
  url: string;
  category?: string;
  date?: string;
  matchCount?: number;
  rank?: number;
}

export const GlobalSearch = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [currentSearchId, setCurrentSearchId] = useState<string | null>(null);
  const [searchStartTime, setSearchStartTime] = useState<number>(0);
  const navigate = useNavigate();

  // Initialize session ID on mount
  useEffect(() => {
    const generateSessionId = () => {
      return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    };
    setSessionId(generateSessionId());
  }, []);

  // Keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenChange(true);
      }
      
      // Escape to close
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setIsLoading(true);
      await performSearch(query);
      setIsLoading(false);
    }, 300); // Debounce search

    return () => clearTimeout(searchTimeout);
  }, [query]);

  const performSearch = async (searchQuery: string) => {
    setSearchStartTime(Date.now());
    const q = searchQuery.toLowerCase();
    const allResults: SearchResult[] = [];

    // Search blog posts using database full-text search
    const { data: blogPosts, error: blogError } = await supabase
      .rpc('search_blog_posts', { search_query: searchQuery });

    if (blogError) {
      console.error('Blog search error:', blogError);
    }

    if (blogPosts && blogPosts.length > 0) {
      blogPosts.forEach(post => {
        // Calculate match counts for highlighting (UI only)
        const titleResult = highlightTextWithCount(post.title, q, true);
        const excerptText = post.content
          ? extractContextSnippet(post.content, q, 80)
          : stripHtmlTags(post.excerpt || '');
        const excerptResult = highlightTextWithCount(excerptText, q, true);
        
        allResults.push({
          id: post.id,
          type: 'blog' as const,
          title: post.title,
          excerpt: excerptText,
          content: post.content,
          url: `/insights/${post.slug}`,
          category: post.category,
          date: post.published_at,
          matchCount: titleResult.matchCount + excerptResult.matchCount,
          rank: post.rank
        });
      });
    }

    // Add static pages that match (with fuzzy matching)
    const staticPages = [
      { title: 'Fractional CISO Services', url: '/services/fractional-ciso-uk', excerpt: 'Part-time expert CISO services for UK businesses' },
      // CE PAUSED — Cyber Essentials search entry hidden while services are paused
      { title: 'Risk Management', url: '/services/risk-management', excerpt: 'Comprehensive cyber security risk management services' },
      { title: 'About 4FOX', url: '/about', excerpt: 'Learn about our cyber security team' },
      { title: 'Contact Us', url: '/contact', excerpt: 'Get in touch with our security experts' },
      
      { title: 'Resources', url: '/resources', excerpt: 'Free security resources and guides' },
      { title: 'Testimonials', url: '/testimonials', excerpt: 'What our clients say about us' },
      { title: 'Threat Intelligence', url: '/threat-intelligence', excerpt: 'Latest cyber security threats and advisories' },
    ];

    staticPages.forEach(page => {
      if (
        page.title.toLowerCase().includes(q) ||
        page.excerpt.toLowerCase().includes(q) ||
        fuzzyMatch(page.title, q) ||
        fuzzyMatch(page.excerpt, q)
      ) {
        const titleResult = highlightTextWithCount(page.title, q, true);
        const excerptResult = highlightTextWithCount(page.excerpt, q, true);
        
        allResults.push({
          id: page.url,
          type: 'service',
          title: page.title,
          excerpt: page.excerpt,
          url: page.url,
          matchCount: titleResult.matchCount + excerptResult.matchCount
        });
      }
    });

    // Sort by database rank first (if available), then match count
    allResults.sort((a, b) => {
      // Blog posts have rank, static pages don't
      if (a.rank !== undefined && b.rank !== undefined) {
        return b.rank - a.rank; // Higher rank = more relevant
      }
      if (a.rank !== undefined) return -1; // Blog posts first
      if (b.rank !== undefined) return 1;
      // Fall back to match count for static pages
      return (b.matchCount || 0) - (a.matchCount || 0);
    });

    setResults(allResults);

    // Log search analytics
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await supabase.functions.invoke('log-search-analytics', {
        body: {
          action: 'log_search',
          query: searchQuery,
          resultCount: allResults.length,
          sessionId,
        },
        headers: session?.session?.access_token 
          ? { Authorization: `Bearer ${session.session.access_token}` }
          : {},
      });
      
      if (response.data?.searchId) {
        setCurrentSearchId(response.data.searchId);
      }
    } catch (error) {
      console.error('Failed to log search analytics:', error);
      // Don't block user experience if analytics fails
    }
  };

  const handleResultClick = async (url: string, result: SearchResult) => {
    // Log click analytics
    if (currentSearchId) {
      const timeToClickMs = Date.now() - searchStartTime;
      
      try {
        const { data: session } = await supabase.auth.getSession();
        await supabase.functions.invoke('log-search-analytics', {
          body: {
            action: 'log_click',
            searchId: currentSearchId,
            clickedResult: {
              id: result.id,
              title: result.title,
              url: result.url,
              type: result.type,
            },
            timeToClickMs,
          },
          headers: session?.session?.access_token 
            ? { Authorization: `Bearer ${session.session.access_token}` }
            : {},
        });
      } catch (error) {
        console.error('Failed to log click analytics:', error);
      }
    }
    
    navigate(url);
    onOpenChange(false);
    setQuery("");
    setCurrentSearchId(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Search 4FOX</DialogTitle>
        </DialogHeader>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search articles, services, resources... (Cmd+K)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10 h-12"
            autoFocus
          />
        </div>

        <div className="overflow-y-auto flex-1 mt-4">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Searching...</div>
          ) : results.length === 0 && query ? (
            <div className="text-center py-8 text-muted-foreground">
              No results found for "{query}"
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Start typing to search across articles, services, and resources
            </div>
          ) : (
            <>
              <div className="mb-3 text-sm text-muted-foreground">
                Showing {results.length} {results.length === 1 ? 'result' : 'results'} for "{query}"
              </div>
              <div className="space-y-2">
                {results.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => handleResultClick(result.url, result)}
                    className="w-full text-left p-4 rounded-lg hover:bg-muted transition-colors border border-border focus:outline-none focus:ring-2 focus:ring-primary"
                    tabIndex={0}
                  >
                    <div className="flex items-start gap-3">
                      <FileText className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold text-sm">
                            {highlightTextWithCount(result.title, query, true).jsx}
                          </h3>
                          {result.type === 'blog' && (
                            <Badge variant="outline" className="text-xs flex-shrink-0">Article</Badge>
                          )}
                          {result.matchCount && result.matchCount > 0 && (
                            <Badge variant="secondary" className="text-xs flex-shrink-0">
                              {result.matchCount} {result.matchCount === 1 ? 'match' : 'matches'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {highlightTextWithCount(result.excerpt, query, true).jsx}
                        </p>
                        {result.date && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {new Date(result.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        )}
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
